import * as Sentry from '@sentry/browser';
import { noop } from 'lodash';
import React from 'react';
import { bool, string, func } from 'prop-types';
import {
  Button,
  AppBar,
  Toolbar,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';

import DeviceSettingsModal from 'Elements/Modals/DeviceSettings';
import { withAppContextHOC } from 'Scenes/_root/appContext';
import { withAnalyticsHOC } from 'Scenes/_root/analyticsContext';
import getHelperTextFromErrors from 'Services/helpers/errorText';
import validate from 'Services/validate';
import MeetingInfoWarnings from './MeetingInfoWarnings';
import styles from './styles.scss';

const nameValidation = {
  required: true,
  minLength: 3,
};

class PatientLogin extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      nameErrors: validate('', nameValidation),
      nameTouched: false,
      joinMeetingErrors: null,
    };
  }

  handleFormSubmit = (event) => {
    const {
      name,
      nameErrors,
    } = this.state;
    const {
      attendeeGuid,
      consultGuid,
      isLoggedIn,
      locationPatientMeeting,
      meetingJoin,
      sendSessionProperties,
      identifyUser,
      trackEvent,
    } = this.props;
    event.preventDefault();
    if (nameErrors) return;
    // connect to meeting
    meetingJoin(name, consultGuid, attendeeGuid).then((success) => {
      if (success) {
        if (!isLoggedIn) {
          Sentry.configureScope((scope) => {
            scope.setTag('user_type', 'patient');
            scope.setUser({ id: attendeeGuid });
          });
          identifyUser(attendeeGuid);
          trackEvent('patient login');
          sendSessionProperties({
            user_type: 'patient',
          });
        }
        // move patient to waiting room
        locationPatientMeeting(consultGuid, attendeeGuid);
      }
    });
  }

  handleNameChange = (event) => {
    this.setState({
      name: event.target.value,
      nameErrors: validate(event.target.value, nameValidation),
      nameTouched: true,
    });
  }

  handleNameBlur = () => {
    this.setState({
      nameTouched: true,
    });
  }

  render() {
    const {
      nameErrors,
      nameTouched,
    } = this.state;
    const {
      isFetching,
      meetingIsActive,
    } = this.props;
    return (
      <React.Fragment>
        <div className={styles.patientLoginWrapper}>
          <div className={styles.heading}>
            <Typography variant='h2'>Join a consult</Typography>
            <Typography variant='body1'>Please enter your details below in order to connect to your consult</Typography>
          </div>
          <MeetingInfoWarnings />
          <form onSubmit={this.handleFormSubmit}>
            <TextField
              autoFocus
              fullWidth
              required
              autoComplete='name'
              error={nameTouched && !!nameErrors}
              helperText={nameTouched && getHelperTextFromErrors(nameErrors)}
              label='Your full name'
              margin='dense'
              name='name'
              onBlur={this.handleNameBlur}
              onChange={this.handleNameChange}
              placeholder='Enter your full name'
              size='small'
              value={this.state.name}
              variant='outlined'
            />
            <DeviceSettingsModal
              inModal={false}
              onClose={noop}
            />
          </form>
        </div>
        <AppBar
          classes={{ root: styles.patientLoginBar }}
          color='default'
        >
          <Toolbar>
            <div className={styles.buttonWrapper}>
              <Button
                fullWidth
                color='primary'
                disabled={isFetching || !!nameErrors || !meetingIsActive}
                onClick={this.handleFormSubmit}
                variant='contained'
              >
                Join your consult
              </Button>
              { isFetching && <CircularProgress className={styles.buttonProgress} size={24} /> }
            </div>
          </Toolbar>
        </AppBar>
        <div className={styles.offset} />
      </React.Fragment>
    );
  }
}

const MapStateProps = {
  isFetching: bool.isRequired,
  meetingIsActive: bool.isRequired,
};

const MapDispatchProps = {
  locationPatientMeeting: func.isRequired,
  meetingJoin: func.isRequired,
  openDeviceSettingsModal: func.isRequired,
};

PatientLogin.propTypes = {
  consultGuid: string.isRequired,
  attendeeGuid: string.isRequired,
  isLoggedIn: bool.isRequired,
  identifyUser: func.isRequired,
  sendSessionProperties: func.isRequired,
  trackEvent: func.isRequired,
  ...MapStateProps,
  ...MapDispatchProps,
};

export default withAnalyticsHOC([
  'identifyUser',
  'sendSessionProperties',
  'trackEvent',
])(withAppContextHOC(['isLoggedIn'])(PatientLogin));
